import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import QueryExplanation from './QueryExplanation';
import ResultView from './ResultView';
import ControlPanel from './ControlPanel';
import ActionPanel from './ActionPanel';
import Card from '../Card';
import AlignedButtonLayout from './AlignedButtonLayout';
import QueryMetadata from './QueryMetadata';
import * as XLSX from 'xlsx';
import { X, Info, ChevronUp, ChevronDown, MessageSquarePlus, Filter, Download, Edit2, Check, X as XIcon, Settings } from 'lucide-react';
import QueryInput from './QueryInput';
import useFollowUpQuery from '../../hooks/useFollowUpQuery';
import { useConfig } from '../../contexts/ConfigContext';
import { processData, formatNumber, detectColumnType } from './dataProcessing';
import DownloadAllButton from './DownloadAllButton';
import { transposeData, pivotData, processTransformedData, revertToOriginal } from './dataTransformation';
import PivotConfigModal from './PivotConfigModal';
import { useBeyondAnalytics } from '../../hooks/useBeyondAnalytics';
import Button from '../Button';




const IntermediatePopup = ({ position, onClick }) => (
  <div 
    className="fixed z-50 cursor-pointer intermediate-popup"
    style={{ 
      left: `${position.x}px`, 
      top: `${position.y}px`,
      transform: 'translate(-50%, -50%)'
    }}
    onClick={onClick}
  >
    <div className="w-10 h-10 rounded-full bg-white shadow-custom animate-popIn flex items-center justify-center">
      <img src="/logo512.png" alt="App Icon" className="w-8 h-8" />
    </div>
  </div>
);

const QueryResultCard = ({ 
  query, 
  currentUser, 
  onSaveQuery, 
  onExplanationClick,
  isExplanationLoading,
  isExplanationPending,
  isDashboardMode = false,
  onSaveCardSettings,
  cardWidth,
  onCardWidthChange,
  onFollowUpQuery,
  onClose,
  isAdmin,
  isAIManager,
  isLoading,
  fetchRecentSearches,
  onTextSelection,
  onClearResult,
  onQueryResult,
  onAddToLibrary,
  saveQuerySettings,
  getQuerySettings,
  saveQueryTitle
}) => {



  const [viewMode, setViewMode] = useState((query.cardSettings && query.cardSettings.viewMode) || 'table');
  const [chartType, setChartType] = useState((query.cardSettings && query.cardSettings.chartType) || 'bar');
  const [selectedXAxis, setSelectedXAxis] = useState((query.cardSettings && query.cardSettings.selectedXAxis) || '');
  const [selectedYAxis, setSelectedYAxis] = useState((query.cardSettings && query.cardSettings.selectedYAxis) || '');
  const [sortColumn, setSortColumn] = useState((query.cardSettings && query.cardSettings.sortColumn) || '');
  const [sortOrder, setSortOrder] = useState((query.cardSettings && query.cardSettings.sortOrder) || 'asc');
  const [processedData, setProcessedData] = useState([]);
  const [isVisible, setIsVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [localCardWidth, setLocalCardWidth] = useState(cardWidth || '100%');
  const [isExplanationCollapsed, setIsExplanationCollapsed] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [statsData, setStatsData] = useState(null);
  const [isNumberFormatted, setIsNumberFormatted] = useState(true);
  const [visibleColumns, setVisibleColumns] = useState(query?.cardSettings?.visibleColumns || []);
  const [columnOrder, setColumnOrder] = useState(query?.cardSettings?.columnOrder || []);
  const [showSummary, setShowSummary] = useState(query?.cardSettings?.showSummary ?? true);
  const [syncScales, setSyncScales] = useState((query.cardSettings && query.cardSettings.syncScales) || false);
  const [isRtl, setIsRtl] = useState(false);
  const [isTransposing, setIsTransposing] = useState(false);
  const [activeFilters, setActiveFilters] = useState(query?.cardSettings?.activeFilters || {});
  const { config } = useConfig();
  const [previousQueryId, setPreviousQueryId] = useState(null);
  const [isNewOrFollowupQuery, setIsNewOrFollowupQuery] = useState(false);
  const [transformationType, setTransformationType] = useState(null);
  const [isTransforming, setIsTransforming] = useState(false);
  const [pivotConfig, setPivotConfig] = useState(null);
  const [showPivotModal, setShowPivotModal] = useState(false);
  const [originalData, setOriginalData] = useState(null);
  const [showTrendLine, setShowTrendLine] = useState(query?.cardSettings?.showTrendLine ?? true);
  const [editingTitle, setEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const editInputRef = useRef(null);
  const [isTransformationComplete, setIsTransformationComplete] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [customColumnTitles, setCustomColumnTitles] = useState({});

  // Memoize the analytics inputs to prevent unnecessary re-processing
  const memoizedResult = useMemo(() => query?.result, [query?.documentId, query?.result?.length]);
  const memoizedSchema = useMemo(() => query?.schema, [query?.documentId, query?.schema?.fields?.length]);
  const memoizedBeyondAnalytics = useMemo(() => query?.beyondAnalytics, [query?.documentId, query?.beyondAnalytics?.analyticsRequired]);

  const { 
    enhancedData: beyondData, 
    enhancedSchema: beyondSchema, 
    isProcessing: isBeyondProcessing,
    hasPredictions
  } = useBeyondAnalytics(
    memoizedResult,
    memoizedSchema,
    memoizedBeyondAnalytics,
    query?.documentId  // Pass the documentId as the fourth parameter
  );

// Use enhanced data if available, otherwise use original
const displayData = beyondData || query?.result;
const displaySchema = beyondSchema || query?.schema;

// Memoize the result object to prevent re-renders
const resultData = useMemo(() => ({
  ...query,
  result: displayData,
  schema: displaySchema
}), [query?.documentId, displayData, displaySchema, query?.explanation]);

// Add this effect to update the query result when Beyond Analytics processing is complete
useEffect(() => {
  // Only update if we have predictions and the data has changed
  if (hasPredictions && beyondData && beyondSchema && onQueryResult && !isBeyondProcessing) {
    // Check if the enhanced data is different from the current query result
    const hasForecasts = beyondSchema.fields.some(field => field.isForecast);
    const currentHasForecasts = query?.schema?.fields?.some(field => field.isForecast);
    
    if (hasForecasts && !currentHasForecasts) {
      console.log('[QueryResultCard] Updating query result with Beyond Analytics data');
      // Update the query result with the enhanced data
      onQueryResult({
        ...query,
        result: beyondData,
        schema: beyondSchema,
        // Mark that this result includes Beyond Analytics enhancements
        hasBeyondAnalytics: true
      });
    }
  }
}, [hasPredictions, beyondData, beyondSchema, isBeyondProcessing, onQueryResult, query?.documentId]);

  const cardRef = useRef(null);
  const popupRef = useRef(null);
  const modalRef = useRef(null);
  const chartRef = useRef(null);

  const {
    selectedText,
    selectedColumns,
    showIntermediatePopup,
    intermediatePopupPosition,
    showFollowUpInput,
    isFromHighlight,
    handleTextSelection,
    handleIntermediatePopupClick,
    handleFollowUpSubmit: hookHandleFollowUpSubmit,
    handleClickOutside,
    handleEscKey,
    setShowFollowUpInput,
    resetFollowUpState
  } = useFollowUpQuery(async (submittedQuery, selectionData) => {
    setShowFollowUpInput(false);
    
    // Get current column configuration
    const currentColumnConfig = {
      visibleColumns,
      columnOrder,
      showSummary,
      viewMode,
      chartType,
      syncScales,
      selectedXAxis,
      selectedYAxis,
      sortColumn,
      sortOrder
    };
  
  
    await onFollowUpQuery(
      submittedQuery, 
      selectionData.text, 
      query.sqlQuery, 
      selectionData.columns,
      currentColumnConfig
    );
  });

  
  const hasNumericFields = useCallback(() => {
    if (query && query.schema && query.schema.fields) {
      // Only check explicit numeric types
      return query.schema.fields.some(field => 
        ['INTEGER', 'FLOAT', 'NUMERIC'].includes(field.type.toUpperCase())
      );
    }
    return false;
  }, [query]);
  
  const showFormatCheckbox = useMemo(() => {
    return hasNumericFields() && query.result && query.result.length > 0;
  }, [hasNumericFields, query.result]);

  useEffect(() => {
    if (query?.cardSettings) {
      
      // Update view mode and chart type - ALWAYS apply these for actions
      if (query.cardSettings.viewMode) {
        setViewMode(query.cardSettings.viewMode);
        console.log('Action: Updated viewMode to', query.cardSettings.viewMode);
      }
      if (query.cardSettings.chartType) {
        setChartType(query.cardSettings.chartType);
        console.log('Action: Updated chartType to', query.cardSettings.chartType);
      }
      
      // Update axes
      if (query.cardSettings.selectedXAxis) {
        setSelectedXAxis(query.cardSettings.selectedXAxis);
      }
      if (query.cardSettings.selectedYAxis) {
        setSelectedYAxis(query.cardSettings.selectedYAxis);
      }
      
      // Update sorting
      if (query.cardSettings.sortColumn) {
        setSortColumn(query.cardSettings.sortColumn);
      }
      if (query.cardSettings.sortOrder) {
        setSortOrder(query.cardSettings.sortOrder);
      }
      
      // Other settings
      if (query.cardSettings.syncScales !== undefined) {
        setSyncScales(query.cardSettings.syncScales);
      }
      if (query.cardSettings.showSummary !== undefined) {
        setShowSummary(query.cardSettings.showSummary);
      }
      if (query.cardSettings.showTrendLine !== undefined) {
        setShowTrendLine(query.cardSettings.showTrendLine);
      }
      
      // Apply number formatting setting from actions
      if (query.cardSettings.isNumberFormatted !== undefined) {
        setIsNumberFormatted(query.cardSettings.isNumberFormatted);
      }
  
      // Load transformation settings
      if (query.cardSettings.transformationType) {
        setTransformationType(query.cardSettings.transformationType);
      }
      if (query.cardSettings.pivotConfig) {
        setPivotConfig(query.cardSettings.pivotConfig);
      }
      // Load custom column titles
      if (query.cardSettings.customColumnTitles) {
        setCustomColumnTitles(query.cardSettings.customColumnTitles);
      }
    }
    // Don't set visibleColumns and columnOrder here, 
    // they are handled by another useEffect
  }, [query?.cardSettings]);


  useEffect(() => {
    // Check if this is a completely different query from the previous one
    const queryId = query?.savedQueryId || query?.id || query?.documentId;
    const isNewQuery = queryId && queryId !== previousQueryId;
    
    if (query && isNewQuery) {
      
      // Update the previous query ID
      setPreviousQueryId(queryId);
      
      // IMPORTANT: Always apply card settings directly from the query.cardSettings
      // This ensures we're using the settings that were combined in runSavedQuery
      if (query.cardSettings) {
          
        // Update view mode and chart type
        if (query.cardSettings.viewMode) {
          setViewMode(query.cardSettings.viewMode);
        }
        if (query.cardSettings.chartType) {
          setChartType(query.cardSettings.chartType);
        }
        
        // Update axes
        if (query.cardSettings.selectedXAxis) {
          setSelectedXAxis(query.cardSettings.selectedXAxis);
        }
        if (query.cardSettings.selectedYAxis) {
          setSelectedYAxis(query.cardSettings.selectedYAxis);
        }
        
        // Update sorting
        if (query.cardSettings.sortColumn) {
          setSortColumn(query.cardSettings.sortColumn);
        }
        if (query.cardSettings.sortOrder) {
          setSortOrder(query.cardSettings.sortOrder);
        }
        
        // Other settings
        if (query.cardSettings.syncScales !== undefined) {
          setSyncScales(query.cardSettings.syncScales);
        }
        if (query.cardSettings.showSummary !== undefined) {
          setShowSummary(query.cardSettings.showSummary);
        }
        if (query.cardSettings.visibleColumns) {
          setVisibleColumns(query.cardSettings.visibleColumns);
        }
        if (query.cardSettings.columnOrder) {
          setColumnOrder(query.cardSettings.columnOrder);
        }
        // Load transformation settings - handle both defined and null values
        if (query.cardSettings.transformationType !== undefined) {
          setTransformationType(query.cardSettings.transformationType);
        }
        if (query.cardSettings.pivotConfig !== undefined) {
          setPivotConfig(query.cardSettings.pivotConfig);
        }
      }
    }
  }, [query]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    const handleOutsideClick = (event) => handleClickOutside(event, modalRef);
    
    const handleKeyDown = (e) => {
      // Handle ESC key for fullscreen
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      } else {
        handleEscKey(e);
      }
    };

    window.addEventListener('resize', handleResize);
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleClickOutside, handleEscKey, isFullscreen]);

  useEffect(() => {
    setLocalCardWidth(cardWidth || '100%');
  }, [cardWidth]);

    // Store current document ID for BigBro to access
    useEffect(() => {
      if (query && query.documentId) {
        localStorage.setItem('currentDocumentId', query.documentId);
      }
      
      return () => {
        // Only clear if this component set it
        if (query && query.documentId && 
            localStorage.getItem('currentDocumentId') === query.documentId) {
          localStorage.removeItem('currentDocumentId');
        }
      };
    }, [query?.documentId]);

    useEffect(() => {
      if (query && query.result && query.result.length > 0) {
        // Use displayData which includes beyondData if available
        const dataToCheck = displayData || query.result;
        const firstRow = dataToCheck[0];
        const keys = Object.keys(firstRow);
        
        // First check schema for DATE or TIMESTAMP columns
        const schemaToCheck = displaySchema || query.schema;
        const schemaDateColumn = keys.find(key => {
          const field = schemaToCheck?.fields?.find(f => f.name === key);
          return field && (field.type.toLowerCase().includes('date') || field.type.toLowerCase().includes('timestamp'));
        });
        
        // Fall back to pattern matching if no schema date column is found
        const dateColumn = schemaDateColumn || keys.find(key => {
          const value = firstRow[key];
          if (typeof value === 'object' && value !== null && 'value' in value) {
            return value.value && typeof value.value === 'string' && 
                   (value.value.match(/^\d{4}-\d{2}-\d{2}/) || value.value.match(/^\d{4}-\d{2}/));
          }
          return value && typeof value === 'string' && 
                 (value.match(/^\d{4}-\d{2}-\d{2}/) || value.match(/^\d{4}-\d{2}/));
        }) || keys[0];
    
        // Find all numeric columns
        const numericColumns = keys.filter(key => {
          const value = firstRow[key];
          return typeof value === 'number' || 
                (typeof value === 'object' && value !== null && typeof value.value === 'number');
        });
    
        // Check for forecast columns
        let forecastColumns = [];
        let primaryForecastColumns = [];
        let originalColumnsWithForecasts = [];
    
        keys.forEach(col => {
          if (col.endsWith(' - forecast') || col.includes(' - forecast (')) {
            forecastColumns.push(col);
            
            // Check if it's a primary forecast (no predictor name in parentheses)
            if (col.endsWith(' - forecast') && !col.includes('(')) {
              primaryForecastColumns.push(col);
            }
            
            // Extract original column name
            const originalCol = col.replace(/ - forecast.*$/, '');
            if (keys.includes(originalCol) && !originalColumnsWithForecasts.includes(originalCol)) {
              originalColumnsWithForecasts.push(originalCol);
            }
          }
        });
    
        let xAxis = query.cardSettings?.selectedXAxis || dateColumn;
        let yAxes;
    
        // Check if cardSettings.selectedYAxis is empty or doesn't exist
        const hasExistingYAxis = query.cardSettings?.selectedYAxis && 
                                Array.isArray(query.cardSettings.selectedYAxis) && 
                                query.cardSettings.selectedYAxis.length > 0;
    
        if (!hasExistingYAxis && forecastColumns.length > 0) {
          // If we have forecasts, automatically select original and PRIMARY forecast only
          const firstOriginalColumn = originalColumnsWithForecasts[0] || numericColumns[0];
          const primaryForecastColumn = primaryForecastColumns.find(col => 
            col.startsWith(`${firstOriginalColumn} - forecast`)
          ) || `${firstOriginalColumn} - forecast`;
          
          yAxes = [firstOriginalColumn, primaryForecastColumn].filter(col => keys.includes(col));
          
          // Automatically enable Compare mode when forecast columns are selected
          if (yAxes.length > 1 && !query.cardSettings?.syncScales) {
            setSyncScales(true);
          }
        } else if (hasExistingYAxis) {
          yAxes = query.cardSettings.selectedYAxis;
        } else {
          yAxes = numericColumns.length > 0 ? [numericColumns[0]] : [keys[0]];
        }
        
        // Convert yAxes to array if it's not already
        if (!Array.isArray(yAxes)) {
          yAxes = [yAxes];
        }
    
        // Validate that yAxes contains actual columns from the data
        const validYAxes = yAxes.filter(axis => keys.includes(axis));
        if (validYAxes.length === 0) {
          // If no valid Y-axes, pick the first numeric column that's not X-axis
          const fallbackY = numericColumns.find(col => col !== xAxis) || keys.find(k => k !== xAxis) || keys[0];
          yAxes = [fallbackY];
        } else {
          yAxes = validYAxes;
        }
    
        // If no Y axes are selected or the only Y axis is the same as X axis
        if (yAxes.length === 0 || (yAxes.length === 1 && yAxes[0] === xAxis)) {
          const availableNumeric = numericColumns.filter(col => col !== xAxis);
          if (availableNumeric.length > 0) {
            yAxes = [availableNumeric[0]];
          } else {
            // If no numeric columns available, find ANY column that's not the X axis
            const nonXAxisColumn = keys.find(k => k !== xAxis);
            yAxes = nonXAxisColumn ? [nonXAxisColumn] : [];
          }
        }

// Final validation: ensure Y axes don't include X axis
yAxes = yAxes.filter(axis => axis !== xAxis);
    
        setSelectedXAxis(xAxis);
        setSelectedYAxis(yAxes);
    
        const isXAxisDate = (() => {
          const value = firstRow[xAxis];
          if (typeof value === 'object' && value !== null && 'value' in value) {
            return value.value && typeof value.value === 'string' && 
                   (value.value.match(/^\d{4}-\d{2}-\d{2}/) || value.value.match(/^\d{4}-\d{2}/));
          }
          return value && typeof value === 'string' && 
                 (value.match(/^\d{4}-\d{2}-\d{2}/) || value.match(/^\d{4}-\d{2}/));
        })();
    
        // Ensure yAxes[0] is a valid column
        const primaryYAxis = keys.includes(yAxes[0]) ? yAxes[0] : (numericColumns[0] || keys[0]);
        const isYAxisNumeric = numericColumns.includes(primaryYAxis);
        const isXAxisNumeric = numericColumns.includes(xAxis);
    
        // Set default sort based on column type
        if (query.cardSettings?.sortColumn && keys.includes(query.cardSettings.sortColumn)) {
          setSortColumn(query.cardSettings.sortColumn);
          setSortOrder(query.cardSettings.sortOrder || 'asc');
        } else {
          if (isXAxisDate || (isXAxisNumeric && isYAxisNumeric)) {
            setSortColumn(xAxis);
            setSortOrder('asc');
          } else if (isYAxisNumeric) {
            setSortColumn(primaryYAxis);
            setSortOrder('desc');
          } else {
            setSortColumn(primaryYAxis);
            setSortOrder('asc');
          }
        }
    
        if (!query.cardSettings?.chartType) {
          setChartType(isXAxisDate ? 'line' : 'bar');
        }
      }
    }, [query, hasPredictions, displayData, displaySchema]);

  useEffect(() => {
    if (!query?.result?.length) return;
  
    const resultColumns = Object.keys(query.result[0]);
  
    // --- decide visible + order ---------------------------------------------
    let newVisible = resultColumns;
    let newOrder   = resultColumns;
  
    if (query.cardSettings?.visibleColumns && query.cardSettings?.columnOrder) {
      const validVis   = query.cardSettings.visibleColumns.filter(c => resultColumns.includes(c));
      const validOrder = query.cardSettings.columnOrder.filter(c => resultColumns.includes(c));
      const extras     = resultColumns.filter(c => !validOrder.includes(c));
      
      // Always include forecast columns in visible columns
      const forecastColumns = resultColumns.filter(c => c.endsWith(' - forecast'));
      const visWithForecasts = [...new Set([...validVis, ...forecastColumns])];
    
      // Only reset visible columns during transformations or if no valid columns exist
      const isTransformation = query.transformationType || query.cardSettings?.transformationType;
      const keepSaved = isTransformation
        ? visWithForecasts.length >= resultColumns.length - 1 // Strict check for transformations
        : visWithForecasts.length > 0; // Lenient check for regular queries - keep if any valid columns exist
    
      newVisible = keepSaved ? visWithForecasts : resultColumns;
      newOrder   = [...validOrder, ...extras];
    } else {
      // Make sure forecast columns are visible by default
      newVisible = resultColumns;
      newOrder = resultColumns;
    }
  
    // --- validate axes -------------------------------------------------------
    const yArr   = Array.isArray(selectedYAxis) ? selectedYAxis : [selectedYAxis];
    const yValid = yArr.every(c => resultColumns.includes(c));
    if (!yValid) {
      const fallbackY = resultColumns.find(c => c !== selectedXAxis) || resultColumns[0];
      setSelectedYAxis([fallbackY]);
    }
  
    // --- commit --------------------------------------------------------------
    setVisibleColumns(newVisible);
    setColumnOrder(newOrder);
    setShowSummary(query.cardSettings?.showSummary ?? true);
  }, [query?.cardSettings, query?.result]);
  

useEffect(() => {
  // Check if explanation exists and is primarily in Hebrew/RTL
  if (query?.explanation) {
    // Use the same detection logic as in QueryExplanation
    const explanationText = typeof query.explanation === 'string' 
      ? query.explanation
      : query.explanation.explanation;
      
    if (explanationText) {
      const hebrewPattern = /[\u0590-\u05FF]/g;
      const hebrewMatches = explanationText.match(hebrewPattern) || [];
      setIsRtl(hebrewMatches.length > explanationText.length * 0.4);
    }
  }
}, [query?.explanation]);

useEffect(() => {
  if (query?.cardSettings?.activeFilters) {
    setActiveFilters(query.cardSettings.activeFilters);
  }
}, [query]);


useEffect(() => {
  if (query) {
  }
}, [query, isDashboardMode]);


useEffect(() => {
  // Get the query ID - dashboard queries use 'id', library queries use 'savedQueryId'
  const queryId = query?.savedQueryId || query?.id;
  
  // Save settings for any query with an ID (both library and dashboard queries)
  // BUT ONLY if transformation is complete
  if (!query || !saveQuerySettings || !queryId || !isTransformationComplete) {
    return;
  }
  
  try {
    const settings = {
      viewMode,
      chartType,
      selectedXAxis,
      selectedYAxis,
      visibleColumns,
      columnOrder,
      syncScales,
      sortColumn,
      sortOrder,
      showSummary,
      activeFilters,
      showTrendLine,
      isNumberFormatted,
      // Add transformation settings
      transformationType,
      pivotConfig: transformationType === 'pivot' ? pivotConfig : null,
      // Add custom column titles
      customColumnTitles
    };
    
    saveQuerySettings(queryId, settings);
  } catch (error) {
    console.error('Error saving query settings:', error);
  }
}, [
  query?.savedQueryId,
  query?.id,
  query?.documentId,
  query?.isFollowUp,
  viewMode, 
  chartType, 
  selectedXAxis, 
  selectedYAxis, 
  visibleColumns, 
  columnOrder, 
  syncScales, 
  sortColumn, 
  sortOrder, 
  showSummary,
  activeFilters,
  showTrendLine,
  isNumberFormatted,
  transformationType,
  pivotConfig,
  isDashboardMode,
  saveQuerySettings,
  isTransformationComplete,
  customColumnTitles
]);


useEffect(() => {
  // Store original data on first load
  // IMPORTANT: Don't overwrite if we already have it, unless it's a completely new query
  const queryId = query?.savedQueryId || query?.documentId || query?.id;
  const isNewQuery = queryId && queryId !== previousQueryId;
  
  if (query?.result && query?.result.length > 0 && (!originalData || isNewQuery)) {
    // Check if this data is already transformed - if so, use the stored original
    if (query.originalResult && query.originalSchema) {
      setOriginalData({
        result: query.originalResult,
        schema: query.originalSchema
      });
    } else {
      // This is fresh, untransformed data
      setOriginalData({
        result: query.result,
        schema: query.schema
      });
    }
  }
}, [query, originalData, previousQueryId]);

const transformationAppliedRef = useRef(false);

useEffect(() => {
  // Only apply if we have data, transformation settings, and haven't applied already
  if (
    query?.result?.length > 0 && 
    query?.cardSettings?.transformationType && 
    !transformationAppliedRef.current
  ) {
    
    // Mark transformation as in-progress
    setIsTransformationComplete(false);
    
    // Define the transformation function
    const applyTransformation = () => {
      if (query.cardSettings.transformationType === 'transpose') {
        // Manually do the transpose logic inline
        setIsTransforming(true);
        try {
          const { transposedData, transposedSchema } = transposeData(
            originalData?.result || query.result, 
            originalData?.schema || query.schema
          );
          
          // Apply the transformation
          if (onQueryResult) {
            const processedData = processTransformedData(transposedData);
            const transformedQuery = {
              ...query,
              result: processedData,
              schema: transposedSchema,
              transformationType: 'transpose',
              // Always preserve the untransformed data for reverting
              originalResult: query.originalResult || originalData?.result || query.result,
              originalSchema: query.originalSchema || originalData?.schema || query.schema
            };
            
            // Update the query result
            onQueryResult(transformedQuery);
            setTransformationType('transpose');
            setIsTransformationComplete(true);
          }
        } catch (error) {
          console.error('Error during transpose:', error);
          setIsTransformationComplete(true);
        } finally {
          setIsTransforming(false);
        }
      } else if (query.cardSettings.transformationType === 'pivot' && query.cardSettings.pivotConfig) {
        // Manually do the pivot logic inline
        setIsTransforming(true);
        try {
          const config = query.cardSettings.pivotConfig;
          const { pivotedData, pivotedSchema } = pivotData(
            originalData?.result || query.result,
            config,
            originalData?.schema || query.schema
          );
          
          // Apply the transformation
          if (onQueryResult) {
            const processedData = processTransformedData(pivotedData);
            
            // IMPORTANT: Preserve user's saved axis selections if they exist
            const preservedSettings = {};
            if (query.cardSettings.selectedXAxis) {
              preservedSettings.selectedXAxis = query.cardSettings.selectedXAxis;
            }
            if (query.cardSettings.selectedYAxis) {
              preservedSettings.selectedYAxis = query.cardSettings.selectedYAxis;
            }
            
            const transformedQuery = {
              ...query,
              result: processedData,
              schema: pivotedSchema,
              transformationType: 'pivot',
              pivotConfig: config,
              // Always preserve the untransformed data for reverting
              originalResult: query.originalResult || originalData?.result || query.result,
              originalSchema: query.originalSchema || originalData?.schema || query.schema,
              // Preserve existing cardSettings including user's axis selections
              cardSettings: {
                ...query.cardSettings,
                ...preservedSettings
              }
            };
            
            // Update the query result
            onQueryResult(transformedQuery);
            setTransformationType('pivot');
            setPivotConfig(config);
            
            // Apply the preserved settings to local state
            if (preservedSettings.selectedXAxis) {
              setSelectedXAxis(preservedSettings.selectedXAxis);
            }
            if (preservedSettings.selectedYAxis) {
              setSelectedYAxis(preservedSettings.selectedYAxis);
            }
            setIsTransformationComplete(true);
          }
        } catch (error) {
          console.error('Error during pivot:', error);
          setIsTransformationComplete(true);
        } finally {
          setIsTransforming(false);
        }
      }
    }; // <-- Function definition ends here
    
    // For action-based transformations, ensure we store original data first
    if (!originalData && !query.originalResult) {
      setOriginalData({
        result: query.result,
        schema: query.schema
      });
      // Use a small delay to ensure state is set
      setTimeout(() => {
        transformationAppliedRef.current = true;
        applyTransformation();
      }, 50);
      return;
    }
    
    transformationAppliedRef.current = true;
    
    // Use setTimeout to break the render cycle dependency
    setTimeout(() => {
      applyTransformation();
    }, 0);
  }
}, [query?.result, query?.cardSettings, originalData, onQueryResult]);

// Reset the ref when query changes
useEffect(() => {
  transformationAppliedRef.current = false;
}, [query?.savedQueryId, query?.documentId, query?.id]);

// Add this new useMemo after the other useMemo hooks (around line 635)
const enhancedSchema = useMemo(() => {
  if (!query?.schema || !query?.result) return query?.schema;
  
  return {
    ...query.schema,
    fields: query.schema.fields.map(field => {
      // Get sample values for this column
      const sampleValues = query.result
        .slice(0, 10)
        .map(row => row[field.name])
        .filter(val => val !== null && val !== undefined);
      
      const detectedType = detectColumnType(field.name, field.type, sampleValues);
      
      return {
        ...field,
        detectedType // Add detected type alongside original type
      };
    })
  };
}, [query?.schema, query?.result]);

// Add this handler function for transformations
const handleTransformationChange = useCallback((type) => {
  if (type === transformationType) return;
  
  // Mark transformation as in-progress when starting
  setIsTransformationComplete(false);
  
  // Revert to original data when clearing the transformation
  if (!type) {
    const baseSchema = originalData?.schema || query.originalSchema;
    const baseResult = originalData?.result || query.originalResult;
    if (onQueryResult && baseSchema && baseResult) {
      setIsTransforming(true);
      setTimeout(() => {
        const cols = baseSchema.fields.map(f => f.name);
        
        // Keep current Y-axis if it's valid, otherwise use first column
        // This prevents the "Select Y-Axis" from briefly showing
        const safeYAxis = Array.isArray(selectedYAxis) && selectedYAxis.length > 0
          ? selectedYAxis.filter(axis => cols.includes(axis))
          : [cols[0]];
        
        // Keep selectedYAxis non-empty to avoid "Select Y-Axis" text
        setSelectedYAxis(safeYAxis.length > 0 ? safeYAxis : [cols[0]]);
        
        // Clear other axis values to trigger the normal effects to run
        setSelectedXAxis('');
        setViewMode('table');
        setChartType('bar');
        
        // Include transformation fields explicitly set to null
        const cleanSettings = {
          viewMode: 'table',
          chartType: 'bar',
          visibleColumns: cols,
          columnOrder: cols,
          showSummary: true,
          transformationType: null,
          pivotConfig: null
        };
        
        const reverted = {
          ...query,
          result: baseResult,
          schema: baseSchema,
          transformationType: null,
          pivotConfig: null,
          cardSettings: cleanSettings
        };
        
        onQueryResult(reverted);

        // Save settings for both library queries (savedQueryId) and dashboard queries (id)
        const queryId = query.savedQueryId || query.id;
        if (queryId && saveQuerySettings) {
          saveQuerySettings(queryId, cleanSettings);
        }
        
        setTransformationType(null);
        setPivotConfig(null);
        setIsTransformationComplete(true);
        setIsTransforming(false);
      }, 250);
    }
    return;
  }
  
  // Handle the transformation types
  if (type === 'transpose') {
    setIsTransforming(true);
    try {
      const { transposedData, transposedSchema } = transposeData(
        query.result, 
        query.schema
      );
      
      // Apply the transformation
      if (onQueryResult) {
        const processedData = processTransformedData(transposedData);
        const transformedQuery = {
          ...query,
          result: processedData,
          schema: transposedSchema,
          transformationType: 'transpose',
          // Store original data if not already saved
          originalResult: query.originalResult || query.result,
          originalSchema: query.originalSchema || query.schema
        };
        
        // Update the query result
        onQueryResult(transformedQuery);
        setTransformationType('transpose');
        setIsTransformationComplete(true);
      }
    } catch (error) {
      console.error('Error during transpose:', error);
      setIsTransformationComplete(true);
    } finally {
      setIsTransforming(false);
    }

  } else if (type === 'pivot') {
    // For pivot, show the configuration modal first
    setShowPivotModal(true);
    setIsTransformationComplete(true); // Modal doesn't block settings
  }
}, [query, onQueryResult, originalData, transformationType, saveQuerySettings]);

const handleToggleTrendLine = useCallback((value) => {
  setShowTrendLine(value);
  if (isDashboardMode && onSaveCardSettings) {
    onSaveCardSettings({
      ...query.cardSettings,
      viewMode,
      chartType,
      syncScales,
      selectedXAxis,
      selectedYAxis,
      sortColumn,
      sortOrder,
      showTrendLine: value
    });
  }
}, [isDashboardMode, onSaveCardSettings, query.cardSettings, viewMode, chartType, syncScales, selectedXAxis, selectedYAxis, sortColumn, sortOrder]);

// Add this handler for applying pivot configuration
const handleApplyPivot = useCallback((config) => {
  setShowPivotModal(false);
  setIsTransforming(true);
  setIsTransformationComplete(false); // Mark as in-progress
  setPivotConfig(config);
  
  try {
    const { pivotedData, pivotedSchema } = pivotData(
      query.result,
      config,
      query.schema
    );
    
    // Apply the transformation
    if (onQueryResult) {
      const processedData = processTransformedData(pivotedData);
      
      // Get all new columns from the pivoted schema
      const newColumns = pivotedSchema.fields.map(f => f.name);
      const rowField = config.rowField; // This is a good default for X-axis
      
      // Find a numeric column for Y-axis (any column except the row field)
      const newYAxis = newColumns.find(col => 
        col !== rowField && 
        pivotedData.length > 0 && 
        typeof pivotedData[0][col] === 'number'
      ) || newColumns.find(col => col !== rowField) || newColumns[0];
      
      // Check if we already have saved settings for this pivot
      const hasSavedAxisSettings = query.cardSettings?.selectedXAxis && query.cardSettings?.selectedYAxis;
      
      // Only update axes if we don't have saved settings
      if (!hasSavedAxisSettings) {
        setSelectedXAxis(rowField);
        setSelectedYAxis([newYAxis]);
      }
      
      setVisibleColumns(newColumns); // Make all pivot columns visible
      setColumnOrder(newColumns);    // Set column order for pivoted data
      
      const transformedQuery = {
        ...query,
        result: processedData,
        schema: pivotedSchema,          // סכימת-Pivot לתצוגה
        response: query.result,         // שמירת התוצאה המקורית
        responseSchema: query.schema,   // שמירת הסכימה המקורית
        transformationType: 'pivot',
        pivotConfig: config,
        originalResult: query.originalResult || query.result,
        originalSchema: query.originalSchema || query.schema,
        cardSettings: {
          ...(query.cardSettings || {}),
          // Only set default axes if not already saved
          selectedXAxis: hasSavedAxisSettings ? query.cardSettings.selectedXAxis : rowField,
          selectedYAxis: hasSavedAxisSettings ? query.cardSettings.selectedYAxis : [newYAxis],
          chartType: query.cardSettings?.chartType || 'bar',
          visibleColumns: newColumns,
          columnOrder: newColumns
        }
      };
      
      // Update the query result
      onQueryResult(transformedQuery);
      setTransformationType('pivot');
      setIsTransformationComplete(true);
    }
  } catch (error) {
    console.error('Error during pivot:', error);
    setIsTransformationComplete(true);
  } finally {
    setIsTransforming(false);
  }
}, [query, onQueryResult]);
  
  
  const handleColumnOrderChange = useCallback((newColumnOrder) => {
    setColumnOrder(newColumnOrder);
    if (isDashboardMode && onSaveCardSettings) {
      onSaveCardSettings({
        ...query.cardSettings,
        viewMode,
        chartType,
        syncScales,
        selectedXAxis,
        selectedYAxis,
        sortColumn,
        sortOrder,
        visibleColumns,
        columnOrder: newColumnOrder,
        showSummary
      });
    }
  }, [isDashboardMode, onSaveCardSettings, query.cardSettings, viewMode, chartType, syncScales, selectedXAxis, selectedYAxis, sortColumn, sortOrder, visibleColumns, showSummary]);
  
  const handleVisibleColumnsChange = useCallback((newVisibleColumns) => {
    setVisibleColumns(newVisibleColumns);
    if (isDashboardMode && onSaveCardSettings) {
      onSaveCardSettings({
        ...query.cardSettings,
        viewMode,
        chartType,
        syncScales,
        selectedXAxis,
        selectedYAxis,
        sortColumn,
        sortOrder,
        visibleColumns: newVisibleColumns,
        columnOrder
      });
    }
  }, [isDashboardMode, onSaveCardSettings, query.cardSettings, viewMode, chartType, syncScales, selectedXAxis, selectedYAxis, sortColumn, sortOrder, columnOrder]);
  
  const handleColumnTitleChange = useCallback((columnName, newTitle) => {
    const updatedTitles = { ...customColumnTitles };
    
    if (newTitle === null || newTitle === '') {
      // Remove custom title if null or empty
      delete updatedTitles[columnName];
    } else {
      // Set new custom title
      updatedTitles[columnName] = newTitle;
    }
    
    // Update local state immediately
    setCustomColumnTitles(updatedTitles);
    
    // Also update the query result to propagate changes immediately
    if (onQueryResult) {
      onQueryResult(prevQuery => ({
        ...prevQuery,
        cardSettings: {
          ...prevQuery.cardSettings,
          customColumnTitles: updatedTitles
        }
      }));
    }
    
    // Save to Firestore via existing mechanism
    const queryId = query?.savedQueryId || query?.id;
    if (queryId && saveQuerySettings) {
      const currentSettings = {
        viewMode,
        chartType,
        selectedXAxis,
        selectedYAxis,
        visibleColumns,
        columnOrder,
        syncScales,
        sortColumn,
        sortOrder,
        showSummary,
        activeFilters,
        showTrendLine,
        isNumberFormatted,
        transformationType,
        pivotConfig: transformationType === 'pivot' ? pivotConfig : null,
        customColumnTitles: updatedTitles
      };
      
      saveQuerySettings(queryId, currentSettings);
    }
  }, [customColumnTitles, query, saveQuerySettings, onQueryResult, viewMode, chartType, selectedXAxis, 
      selectedYAxis, visibleColumns, columnOrder, syncScales, sortColumn, sortOrder, 
      showSummary, activeFilters, showTrendLine, isNumberFormatted, transformationType, pivotConfig]);

  const handleFollowUpButtonClick = () => {
    resetFollowUpState();
    setShowFollowUpInput(true);
  };
  
  const handleNumberFormatToggle = useCallback((checked) => {
    setIsNumberFormatted(checked);
    if (isDashboardMode && onSaveCardSettings) {
      onSaveCardSettings({ ...query.cardSettings, isNumberFormatted: checked });
    }
  }, [isDashboardMode, onSaveCardSettings, query.cardSettings]);
  
  const resultDimensions = useMemo(() => {
    return query && query.result && query.result.length > 0 ? Object.keys(query.result[0]).length : 0;
  }, [query]);

  const isSingleValue = useMemo(() => {
    return query && query.result && query.result.length === 1 && resultDimensions === 1;
  }, [query, resultDimensions]);

  const showControls = useMemo(() => {
    return query && query.result && query.result.length > 1 && resultDimensions >= 2;
  }, [query, resultDimensions]);

  const canShowGraph = useMemo(() => {
    if (!query || !query.result || query.result.length === 0) return false;
    if (query.result.length === 1 && resultDimensions <= 3) return false;
    return query.result.length > 1 && resultDimensions >= 2 && hasNumericFields();
  }, [query, resultDimensions, hasNumericFields]);

  const handleCardWidthChange = useCallback((newWidth) => {
    if (!isMobile && onCardWidthChange) {
      setLocalCardWidth(newWidth);
      onCardWidthChange(newWidth);
      if (isDashboardMode && onSaveCardSettings) {
        onSaveCardSettings({ cardWidth: newWidth });
      }
    }
  }, [onCardWidthChange, isMobile, isDashboardMode, onSaveCardSettings]);

  const handleViewModeChange = useCallback((newViewMode) => {
    setViewMode(newViewMode);
    if (newViewMode !== 'table') {
      setChartType(newViewMode);
    }
    if (isDashboardMode && onSaveCardSettings) {
      onSaveCardSettings({ 
        ...query.cardSettings, 
        viewMode: newViewMode, 
        chartType: newViewMode !== 'table' ? newViewMode : chartType,
        syncScales,
        selectedXAxis,
        selectedYAxis,
        sortColumn,
        sortOrder
      });
    }
  }, [isDashboardMode, onSaveCardSettings, query.cardSettings, syncScales, chartType, selectedXAxis, selectedYAxis, sortColumn, sortOrder]);

  const handleSyncScalesChange = useCallback((newSyncValue) => {
    setSyncScales(newSyncValue);
    if (isDashboardMode && onSaveCardSettings) {
      onSaveCardSettings({
        ...query.cardSettings,
        viewMode,
        chartType,
        syncScales: newSyncValue,
        selectedXAxis,
        selectedYAxis,
        sortColumn,
        sortOrder
      });
    }
  }, [isDashboardMode, onSaveCardSettings, query.cardSettings, viewMode, chartType, selectedXAxis, selectedYAxis, sortColumn, sortOrder]);

  const handleShowSummaryChange = useCallback((newShowSummary) => {
    setShowSummary(newShowSummary);
    if (isDashboardMode && onSaveCardSettings) {
      onSaveCardSettings({
        ...query.cardSettings,
        viewMode,
        chartType,
        syncScales,
        selectedXAxis,
        selectedYAxis,
        sortColumn,
        sortOrder,
        showSummary: newShowSummary
      });
    }
  }, [isDashboardMode, onSaveCardSettings, query.cardSettings, viewMode, chartType, syncScales, selectedXAxis, selectedYAxis, sortColumn, sortOrder]);

  const handleExcelDownload = useCallback(() => {
    if (query.result && query.result.length > 0) {
      const worksheet = XLSX.utils.json_to_sheet(query.result);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
      XLSX.writeFile(workbook, "query_result.xlsx");
    }
  }, [query.result]);

  const handleCloseCard = () => {
    if (onClose) {
      onClose(); // This will trigger the parent's handleClose which handles everything
    }
  };

  const handleSortChange = useCallback((column, order) => {
    setSortColumn(column);
    setSortOrder(order);
    if (isDashboardMode && onSaveCardSettings) {
      onSaveCardSettings({ ...query.cardSettings, sortColumn: column, sortOrder: order });
    }
  }, [isDashboardMode, onSaveCardSettings, query.cardSettings]);

  const handleAxisChange = useCallback((axis, value) => {
    if (axis === 'x') {
      setSelectedXAxis(value);
      if (isDashboardMode && onSaveCardSettings) {
        onSaveCardSettings({ 
          ...query.cardSettings,
          selectedXAxis: value,
          viewMode,
          chartType,
          syncScales,
          sortColumn,
          sortOrder
        });
      }
    } else if (axis === 'y') {
      const yAxes = Array.isArray(value) ? value : [value];
      setSelectedYAxis(yAxes);
      if (isDashboardMode && onSaveCardSettings) {
        onSaveCardSettings({ 
          ...query.cardSettings,
          selectedYAxis: yAxes,
          viewMode,
          chartType,
          syncScales,
          sortColumn,
          sortOrder
        });
      }
    }
  }, [isDashboardMode, onSaveCardSettings, query.cardSettings, viewMode, chartType, syncScales, sortColumn, sortOrder]);

  const getCardTitle = useCallback(() => {
    // First check if there is a user-specific custom title (same as QueryLibrary)
    if (query.userCustomTitles && query.userCustomTitles[currentUser?.email]) {
      return query.userCustomTitles[currentUser?.email];
    }
    
    // Check if it's in dashboard mode with a name
    if (isDashboardMode && query.name) {
      // For backwards compatibility, prefer query.name in dashboard mode
      return query.name;
    }
    
    // Fall back to explanation title if available
    if (query.explanation && query.explanation.title) {
      return query.explanation.title;
    }
    
    // Then try query name
    if (query.name) {
      return query.name;
    }
    
    // Then try userQuery
    if (query.userQuery) {
      let titleText = query.userQuery;
      
      // Check if it's a follow-up query
      const isFollowUp = query.isFollowUp || 
                         titleText.toLowerCase().includes('follow-up') || 
                         titleText.toLowerCase().includes('follow up') ||
                         (query.originalQuery && query.originalQuery !== query.userQuery);
    
      if (isFollowUp) {
        titleText = titleText.replace(/^(follow-up:|follow up:)\s*/i, '');
        titleText = `Follow-up: ${titleText}`;
      }
    
      // Truncate if not in dashboard mode
      if (!isDashboardMode) {
        const words = titleText.split(' ');
        if (words.length > 10) {
          titleText = `${words.slice(0, 10).join(' ')}...`;
        }
      }
      
      return titleText;
    }
    
    // Final fallback
    return "Query Result";
  }, [query.name, query.userQuery, query.isFollowUp, query.originalQuery, isDashboardMode, query.explanation, query.userCustomTitles, currentUser?.email]);

  const cardStyle = useMemo(() => {
    const baseStyle = "bg-white shadow-card rounded-lg p-4 mb-4 flex flex-col";
    if (isDashboardMode) {
      return `${baseStyle}`;
    } else {
      return `${baseStyle} border-l-4 border-secondary bg-gradient-to-r from-secondary/5 to-secondary/20`;
    }
  }, [isDashboardMode]);

  const toggleExplanationCollapse = () => {
    setIsExplanationCollapsed(prev => !prev);
  };

  const toggleEditMode = () => {
    setIsEditMode(prev => !prev);
  };

  const calculateStats = useCallback(() => {
    if (query && query.result && query.result.length > 0 && selectedYAxis) {
      const yAxis = Array.isArray(selectedYAxis) ? selectedYAxis[0] : selectedYAxis;
      const values = query.result.map(row => parseFloat(row[yAxis])).filter(val => !isNaN(val));
      
      if (values.length > 0) {
        const sum = values.reduce((acc, val) => acc + val, 0);
        const avg = sum / values.length;

        setStatsData({
          sum: sum,
          avg: avg
        });
      } else {
        setStatsData(null);
      }
    } else {
      setStatsData(null);
    }
  }, [query, selectedYAxis]);

  useEffect(() => {
    calculateStats();
  }, [calculateStats]);


  const handleFollowUpSubmit = async (submittedQuery, selectionData) => {
    const allColumns = query.result && query.result.length > 0 
      ? Object.keys(query.result[0]) 
      : [];
      
    const hiddenColumns = allColumns.filter(col => !visibleColumns.includes(col));
    
    const currentColumnConfig = {
      visibleColumns,
      columnOrder,
      showSummary,
      viewMode,
      chartType,
      syncScales,
      selectedXAxis,
      selectedYAxis,
      sortColumn,
      sortOrder,
      activeFilters  // Include active filters in the column config
    };


    await onFollowUpQuery(
      submittedQuery, 
      selectedText, 
      query.sqlQuery, 
      selectedColumns,
      {
        ...currentColumnConfig,
        valueContext: selectionData?.valueContext || query.valueContext,
        isFromValueSearchList: selectionData?.isFromValueSearchList || false,
        activeFilters // Include at the top level too for backward compatibility
      }
    );

    resetFollowUpState();
    setShowFollowUpInput(false);
  };

  const handleTranspose = async () => {
    if (!query.documentId || !config) return;
    
    setIsTransposing(true);
    try {
      if (query.isTransposed) {
        // When reverting, we should use the original response and schema
        const updatedQuery = {
          ...query,
          result: query.response || query.result,
          schema: query.responseSchema || query.schema,
          isTransposed: false
        };
  
        if (typeof onQueryResult === 'function') {
          onQueryResult(updatedQuery);
        }
      } else {
        // Make BE call when transposing from original to transposed
        const response = await fetch(
          `https://us-central1-${config.core.projectId}.cloudfunctions.net/transposeQueryResult`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              data: {
                documentId: query.documentId,
                revert: false
              }
            })
          }
        );
  
        const result = await response.json();
        
        if (!result.data.success) {
          throw new Error(result.data.error || 'Failed to transpose data');
        }
  
        // Create a schema for transposed data
        const transposedSchema = {
          fields: Object.keys(result.data.result[0]).map(key => ({
            name: key,
            // Infer type based on the first row that isn't the header row
            type: key === 'Field' ? 'STRING' : 
                  result.data.result[1] && !isNaN(result.data.result[1][key]) ? 'FLOAT' : 'STRING'
          }))
        };
  
        // For transposed data, we'll process numeric values directly
        const processedResult = result.data.result.map(row => {
          const processedRow = { ...row };
          if (row.Field === 'total_sales') {
            // Process numeric values
            Object.keys(row).forEach(key => {
              if (key !== 'Field' && !isNaN(parseFloat(row[key]))) {
                processedRow[key] = formatNumber(parseFloat(row[key]), row.Field);
              }
            });
          } else if (row.Field === 'total_transactions' || row.Field === 'unique_customers') {
            // Remove leading zeros from these fields
            Object.keys(row).forEach(key => {
              if (key !== 'Field') {
                processedRow[key] = parseInt(row[key], 10).toString();
              }
            });
          }
          return processedRow;
        });
  
        const updatedQuery = {
          ...query,
          result: processedResult,
          schema: transposedSchema,
          isTransposed: true,
          // Store original data for reversion
          response: query.result,
          responseSchema: query.schema
        };
  
        if (typeof onQueryResult === 'function') {
          onQueryResult(updatedQuery);
        }
      }
    } catch (error) {
      console.error('Error transposing data:', error);
    } finally {
      setIsTransposing(false);
    }
  };

  const handleRemoveFilter = (filter) => {
    console.log(`Removing filter: ${filter}`);
    const followUpQuery = `recalculate without filter: ${filter}`;
    
    if (onFollowUpQuery) {
      // Get current column configuration
      const currentColumnConfig = {
        visibleColumns,
        columnOrder,
        showSummary,
        viewMode,
        chartType,
        syncScales,
        selectedXAxis,
        selectedYAxis,
        sortColumn,
        sortOrder,
        activeFilters
      };
      
      onFollowUpQuery(
        followUpQuery, 
        '', // No highlighted text
        query.sqlQuery, 
        [], // No selected columns
        currentColumnConfig
      );
    }
  };

  const handleRemoveTable = (table) => {
    const followUpQuery = `recalculate without table: ${table}`;
    
    if (onFollowUpQuery) {
      // Get current column configuration
      const currentColumnConfig = {
        visibleColumns,
        columnOrder,
        showSummary,
        viewMode,
        chartType,
        syncScales,
        selectedXAxis,
        selectedYAxis,
        sortColumn,
        sortOrder,
        activeFilters
      };
      
      onFollowUpQuery(
        followUpQuery, 
        '', // No highlighted text
        query.sqlQuery, 
        [], // No selected columns
        currentColumnConfig
      );
    }
  };

  const handleStartEdit = (e) => {
    e.stopPropagation();
    setEditingTitle(true);
    
    // Use custom title if it exists, otherwise use the query name
    const currentTitle = getCardTitle();
    setEditTitle(currentTitle);
    
    // Focus the input after a short delay
    setTimeout(() => {
      if (editInputRef.current) {
        editInputRef.current.focus();
        editInputRef.current.select();
      }
    }, 50);
  };

  const handleCancelEdit = (e) => {
    e.stopPropagation();
    setEditingTitle(false);
    setEditTitle('');
  };

  const handleSaveTitle = async (e) => {
    e.stopPropagation();
    
    if (editTitle.trim() === '' || !query.id || !saveQueryTitle) return;
    
    // Save the new title
    await saveQueryTitle(query.id, editTitle);
    
    // For dashboard mode, trigger a refresh of just this query
    if (isDashboardMode && onQueryResult) {
      // Update the local query object to show the new title immediately
      const updatedQuery = {
        ...query,
        userCustomTitles: {
          ...(query.userCustomTitles || {}),
          [currentUser?.email]: editTitle.trim()
        }
      };
      onQueryResult(updatedQuery);
    }
    
    // Exit edit mode
    setEditingTitle(false);
    setEditTitle('');
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSaveTitle(e);
    } else if (e.key === 'Escape') {
      handleCancelEdit(e);
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="relative">
      <Card 
        ref={cardRef}
        className={`overflow-hidden relative flex flex-col z-0 ${cardStyle}`}
        style={{ 
          width: isMobile ? '100%' : (isDashboardMode ? localCardWidth : '100%'),
          minHeight: '400px',
        }}
      >
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2 flex-grow max-w-[85%]">
            {editingTitle && isDashboardMode ? (
              <div className="flex items-center gap-1 flex-grow">
                <input
                  ref={editInputRef}
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  onKeyDown={handleTitleKeyDown}
                  onClick={(e) => e.stopPropagation()}
                  className="flex-1 text-lg border border-purple-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
                  style={{ direction: isRtl ? 'rtl' : 'ltr' }}
                />
                <button
                  onClick={handleSaveTitle}
                  className="text-green-500 hover:text-green-600 p-1"
                  title="Save title"
                >
                  <Check size={18} />
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="text-gray-400 hover:text-gray-500 p-1"
                  title="Cancel"
                >
                  <XIcon size={18} />
                </button>
              </div>
            ) : (
              <div className="group flex items-center gap-1 max-w-[90%]">
                <h3 className="text-lg font-semibold text-gray-700 break-words md:truncate" style={{ direction: isRtl ? 'rtl' : 'ltr' }}>
                  {getCardTitle()}
                </h3>
                {isDashboardMode && saveQueryTitle && (
                  <button
                    onClick={handleStartEdit}
                    className="text-gray-400 hover:text-primary opacity-0 group-hover:opacity-100 transition-opacity p-1"
                    title="Edit title"
                  >
                    <Edit2 size={16} />
                  </button>
                )}
              </div>
            )}
            {query?.beyondAnalytics?.analyticsRequired && (
              <span className="ml-4 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                Analytics Active
              </span>
            )}
            {(!isDashboardMode || query.isFollowUp) && query.explanation && query.explanation.filters?.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent/10 text-gray-800 text-xs font-medium ml-2">
                <Filter size={12} />
                {isRtl ? "פילטרים פעילים" : "Filters Active"}
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-2 ml-4">
            {/* Edit mode button for dashboard mode */}
            {isDashboardMode && (
              <Button
                onClick={toggleEditMode}
                icon={isEditMode ? X : Settings}
                className={`text-sm h-9 px-2 sm:px-3 py-2 ${
                  isEditMode
                    ? 'bg-gray-700 text-white font-semibold'
                    : 'text-gray-700'
                } rounded-md transition-all duration-200`}
                title={isMobile ? undefined : (isEditMode ? 'Close Edit' : 'Edit')}
              >
                <span className="hidden sm:inline">{isEditMode ? 'Close Edit' : 'Edit'}</span>
              </Button>
            )}
            {(!isDashboardMode || query.isFollowUp) && query.explanation && (
              <button 
                onClick={toggleExplanationCollapse}
                className="p-1 rounded-full hover:bg-gray-100"
                aria-label={isExplanationCollapsed ? "Expand explanation" : "Collapse explanation"}
              >
                {isExplanationCollapsed ? (
                  <ChevronDown size={18} className="text-gray-500" />
                ) : (
                  <ChevronUp size={18} className="text-gray-500" />
                )}
              </button>
            )}
            {!isDashboardMode && (
              <button
                onClick={handleCloseCard}
                className="p-1 rounded-full transition-colors duration-200 hover:bg-gray-200"
                aria-label="Close result"
              >
                <X size={18} className="text-gray-700" />
              </button>
            )}
          </div>
        </div>

        {(!isDashboardMode || query.isFollowUp || isExplanationPending) && (query.explanation || isExplanationLoading || isExplanationPending) && (
        <div className={`mb-4 transition-all duration-300 ${isExplanationCollapsed ? 'h-0 opacity-0 overflow-hidden' : 'h-auto opacity-100'}`}>
          <QueryExplanation
            explanation={query.explanation}
            isCollapsed={isExplanationCollapsed}
            onToggleCollapse={toggleExplanationCollapse}
            className="mb-4"
            isLoading={isExplanationLoading || isExplanationPending}
            onRemoveFilter={handleRemoveFilter}
            onRemoveTable={handleRemoveTable}
            isAdmin={isAdmin}
          />
        </div>
      )}

<AlignedButtonLayout
          viewMode={viewMode}
          setViewMode={handleViewModeChange}
          chartType={chartType}
          setChartType={setChartType}
          canShowGraph={canShowGraph}
          isSingleValue={isSingleValue}
          hasResults={query.result && query.result.length > 0}
          isMobile={isMobile}
          isDashboardMode={isDashboardMode}
          isEditMode={isEditMode}
          toggleEditMode={toggleEditMode}
          statsData={statsData}
          yAxisLabel={selectedYAxis}
          isNumberFormatted={isNumberFormatted}
          onNumberFormatToggle={handleNumberFormatToggle}
          showFormatCheckbox={showFormatCheckbox}
          isTransposed={query.isTransposed}
          transformationType={transformationType}
          onTransformationChange={handleTransformationChange}
          isTransforming={isTransforming}
          showTrendLine={showTrendLine}
          onToggleTrendLine={handleToggleTrendLine}
          isFullscreen={isFullscreen}
          onToggleFullscreen={() => setIsFullscreen(!isFullscreen)}
        />

        {(isDashboardMode ? isEditMode : viewMode !== 'table') && (
          <ControlPanel
            selectedXAxis={selectedXAxis}
            setSelectedXAxis={(value) => handleAxisChange('x', value)}
            selectedYAxis={selectedYAxis}
            setSelectedYAxis={(value) => handleAxisChange('y', value)}
            sortColumn={sortColumn}
            setSortColumn={(column) => handleSortChange(column, sortOrder)}
            sortOrder={sortOrder}
            setSortOrder={(order) => handleSortChange(sortColumn, order)}
            columns={displayData && displayData.length > 0 ? Object.keys(displayData[0]) : []}
            schema={displaySchema}
            isVisible={true}
            viewMode={viewMode}
            visibleColumns={visibleColumns}
            setVisibleColumns={handleVisibleColumnsChange}
            columnOrder={columnOrder}
            setColumnOrder={handleColumnOrderChange}
            showSummary={showSummary}
            setShowSummary={handleShowSummaryChange}
            isTransposed={query.isTransposed}
            onTranspose={handleTranspose}
            isTransposing={isTransposing}
            isDashboardMode={isDashboardMode}
          />
        )}

        <div className="flex-grow overflow-y-auto mt-4 pb-2">
        {query.truncationMessage && (
          <div className="mb-4 p-3 rounded bg-blue-100 text-blue-800 flex items-center justify-between relative">
            <div className="flex items-center flex-grow">
              <Info className="mr-2 flex-shrink-0" size={18} />
              <p>
                {query.truncationMessage}
                {query.totalCount && !query.truncationMessage.includes(query.totalCount.toLocaleString())}
              </p>
            </div>
            <div className="flex-shrink-0 ml-4" style={{ position: 'relative', zIndex: 100 }}>
              <DownloadAllButton 
                documentId={query.documentId}
                userId={currentUser?.email || query.userId || 'anonymous'}
                fileName={getCardTitle()}
                disabled={!query.documentId}
                icon={Download}
                title=""
                className="px-3 py-1.5 bg-white text-primary rounded-full hover:bg-gray-50 shadow-sm hover:shadow transition-all duration-200 flex items-center border border-gray-200"
                buttonText="Download All"
              />
            </div>
          </div>
        )}
        <ResultView 
          result={resultData}
          query={query}
          viewMode={viewMode}
          chartType={chartType}
          selectedXAxis={selectedXAxis}
          selectedYAxis={selectedYAxis}
          setSelectedYAxis={setSelectedYAxis}
          sortColumn={sortColumn}
          sortOrder={sortOrder}
          isSingleValue={isSingleValue}
          onProcessedDataChange={setProcessedData}
          isNumberFormatted={isNumberFormatted}
          onTextSelection={handleTextSelection}
          visibleColumns={visibleColumns}
          columnOrder={columnOrder}
          onSortChange={handleSortChange}
          chartRef={chartRef}
          showSummary={showSummary}
          syncScales={syncScales}
          onSyncScalesChange={handleSyncScalesChange}
          onVisibleColumnsChange={handleVisibleColumnsChange}
          onColumnOrderChange={handleColumnOrderChange}
          onActiveFiltersChange={setActiveFilters}
          activeFilters={activeFilters}
          transformationType={transformationType}
          // Add trend line props
          showTrendLine={showTrendLine}
          onToggleTrendLine={handleToggleTrendLine}
          isAdmin={isAdmin}
          isFullscreen={isFullscreen}
          onFullscreenClose={() => setIsFullscreen(false)}
          cardTitle={getCardTitle()}
          customColumnTitles={customColumnTitles}
          onColumnTitleChange={handleColumnTitleChange}
        />
        </div>

        <div className="mt-auto flex justify-between items-center">
        {isDashboardMode ? (
          // Original circular button for dashboard mode
          <button
            onClick={handleFollowUpButtonClick}
            className="p-1.5 rounded-full transition-all duration-200 bg-white hover:bg-gray-100 border border-gray-200 shadow-sm hover:shadow-md"
            title="Ask follow-up question"
            style={{
              transform: 'scale(1)',
              transition: 'transform 0.2s ease-in-out'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            <img src="/favicon.ico" alt="App Icon" className="w-6 h-6" />
          </button>
        ) : (
          // New input-style design for regular queries
          <div 
            onClick={handleFollowUpButtonClick}
            className="bg-gray-800 rounded-full px-4 py-2 flex items-center gap-2 cursor-pointer hover:ring-2 hover:ring-secondary transition-all duration-200"
          >
            <img src="/favicon.ico" alt="App Icon" className="w-5 h-5" />
            <span className="text-gray-400">Ask a follow-up question</span>
          </div>
        )}
          <ActionPanel 
            result={{
              ...query,
              processedData: processedData,
              cardSettings: {
                viewMode,
                chartType,
                selectedXAxis,
                selectedYAxis,
                visibleColumns,
                columnOrder,
                syncScales,
                sortColumn,
                sortOrder,
                showSummary,
                activeFilters,
                // Include transformation info in card settings
                transformationType: transformationType,
                pivotConfig: pivotConfig
              }
            }}
            onSaveQuery={onSaveQuery}
            onAddToLibrary={onAddToLibrary}
            isSavedQuery={query.isSavedQuery}
            onExplanationClick={onExplanationClick}
            isExplanationLoading={isExplanationLoading}
            isExplanationPending={isExplanationPending}
            explanation={query.explanation}
            isDashboardMode={isDashboardMode}
            isAdmin={isAdmin}
            isAIManager={isAIManager}
            isEmptyResult={!query.result || query.result.length === 0}
            isInDashboard={isDashboardMode}
            downloadExcel={handleExcelDownload}
            viewMode={viewMode}
            chartRef={chartRef}
            cardTitle={getCardTitle()}
            statsData={statsData}
            selectedYAxis={selectedYAxis}
            truncationMessage={query.truncationMessage}
            isMobile={isMobile}
            visibleColumns={visibleColumns}
          /> 
        </div>
      </Card>

      {/* Add PivotConfigModal for pivot table configuration */}
      <PivotConfigModal
        isOpen={showPivotModal}
        onClose={() => setShowPivotModal(false)}
        onApply={handleApplyPivot}
        columns={query?.result && query?.result.length > 0 ? Object.keys(query.result[0]) : []}
        schema={enhancedSchema}
        initialConfig={pivotConfig}
      />

      {showIntermediatePopup && (
        <IntermediatePopup 
          position={intermediatePopupPosition} 
          onClick={handleIntermediatePopupClick} 
        />
      )}

      {showFollowUpInput && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div ref={modalRef} className="bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl">
            <h3 className="text-lg font-semibold mb-4">Follow-up Query</h3>
            {isLoading ? (
              <div className="flex justify-center items-center h-24">
                <p>Loading...</p>
              </div>
            ) : (
              <>
                <QueryInput
                  onSubmit={handleFollowUpSubmit}
                  isLoading={isLoading}
                  isFollowUpMode={true}
                  selectedText={selectedText}
                  selectedColumns={selectedColumns}
                  isCardTitle={getCardTitle()}
                  fetchRecentSearches={fetchRecentSearches}
                />
              </>
            )}
            <button 
              className="mt-4 text-sm text-gray-600 hover:text-gray-800"
              onClick={() => {
                setShowFollowUpInput(false);
                resetFollowUpState();
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QueryResultCard;